import React from 'react';

import { AlertCircle, AlertTriangle, CheckCircle, Info, X } from 'lucide-react';

export interface AlertProps extends React.HTMLAttributes<HTMLDivElement> {
	variant?: 'success' | 'error' | 'warning' | 'info';
	title?: string;
	dismissible?: boolean;
	onDismiss?: () => void;
	icon?: boolean;
	children: React.ReactNode;
}

const Alert: React.FC<AlertProps> = ({
	variant = 'info',
	title,
	dismissible = false,
	onDismiss,
	icon = true,
	className = '',
	children,
	...props
}) => {
	// Variant configurations using clean utility classes
	const variantConfig = {
		success: {
			container: `bg-success-50 border-success-200 text-success-800`,
			icon: CheckCircle,
			iconColor: 'text-success-500',
			titleColor: 'text-success-800',
		},
		error: {
			container: `bg-error-50 border-error-200 text-error-800`,
			icon: AlertCircle,
			iconColor: 'text-error-500',
			titleColor: 'text-error-800',
		},
		warning: {
			container: `bg-warning-50 border-warning-200 text-warning-800`,
			icon: AlertTriangle,
			iconColor: 'text-warning-500',
			titleColor: 'text-warning-800',
		},
		info: {
			container: `bg-primary-50 border-primary-200 text-primary-800`,
			icon: Info,
			iconColor: 'text-primary-500',
			titleColor: 'text-primary-800',
		},
	};

	const config = variantConfig[variant];
	const IconComponent = config.icon;

	return (
		<div
			className={`
        border rounded-lg p-4
        font-primary
        ${config.container}
        ${className}
      `
				.trim()
				.replace(/\s+/g, ' ')}
			{...props}
		>
			<div className="flex items-start">
				{icon && (
					<div className="flex-shrink-0">
						<IconComponent className={`w-5 h-5 ${config.iconColor}`} />
					</div>
				)}

				<div className={`${icon ? 'ml-3' : ''} flex-1`}>
					{title && (
						<h3 className={`text-sm font-medium ${config.titleColor} mb-1`}>
							{title}
						</h3>
					)}
					<div className="text-sm">{children}</div>
				</div>

				{dismissible && (
					<div className="flex-shrink-0 ml-3">
						<button
							onClick={onDismiss}
							className={`
                inline-flex rounded-md p-1.5 hover:bg-black hover:bg-opacity-10
                focus:outline-none focus:ring-2 focus:ring-offset-2
                ${config.iconColor}
              `}
						>
							<X className="w-4 h-4" />
						</button>
					</div>
				)}
			</div>
		</div>
	);
};

export default Alert;
